import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";

import Button from "@components/Button";
import DatePickerInput from "@components/DatePicker/DatePickerInput";
import Dialog from "@components/Dialog";
import Input from "@components/Input";
import Select, { TOption } from "@components/Select";
import SizedBox from "@components/SizedBox";
import { Row } from "@components/Flex";
import { useLang, useMoment } from "@src/hooks/useLang";
import { Season, getFieldsList } from "@services/fieldsService";

interface IProps {
  visible: boolean;
  // All existing seasons, used to prefill the start date (day after the latest
  // season ends) and to build the "carry boundaries from" source list.
  seasons: Season[];
  onClose?: () => void;
  onSubmit: (from: Date, to: Date, copyFromSeasonId?: string) => void;
}

const DONT_COPY = "";

const CreateSeasonDialog: React.FC<IProps> = ({ onSubmit, seasons, ...rest }) => {
  const lang = useLang();
  const moment = useMoment();

  // Prefill start to the day after the latest existing season ends.
  const latestEnd = seasons.reduce<Date | undefined>((acc, s) => {
    return !acc || s.to.getTime() > acc.getTime() ? s.to : acc;
  }, undefined);

  let start = moment().startOf("year");
  if (latestEnd) {
    start = moment(latestEnd).add(1, "day");
  }
  const end = start.clone().add(1, "year");

  const seasonName = (start: Date, end: Date): string => {
    const from = moment(start);
    const to = moment(end);

    if (from.year() !== to.year()) {
      return from.format("YYYY MMM") + " - " + to.format("YYYY MMM");
    }
    if (from.month() !== to.month()) {
      return from.format("YYYY MMM") + " - " + to.format("MMM");
    }
    return from.format("YYYY MMM DD") + " - " + to.format("DD");
  };

  const [from, setStartDate] = useState<Date | null>(start.toDate());
  const [to, setEndDate] = useState<Date | null>(end.toDate());

  // Seasons that actually have fields — the only meaningful copy sources.
  // Empty by default so the "only default season with no boundaries" case
  // hides the picker entirely.
  const [copyCandidates, setCopyCandidates] = useState<Season[]>([]);
  const [copyFrom, setCopyFrom] = useState<string>(DONT_COPY);

  // When the dialog opens, discover which existing seasons have fields.
  useEffect(() => {
    if (!rest.visible) return;
    let cancelled = false;

    // Reset date inputs to the freshly computed defaults on each open.
    setStartDate(start.toDate());
    setEndDate(end.toDate());

    Promise.all(
      seasons.map((s) =>
        getFieldsList(s.id)
          .then((fields) => ({ season: s, hasFields: fields.length > 0 }))
          .catch(() => ({ season: s, hasFields: false }))
      )
    ).then((results) => {
      if (cancelled) return;
      const candidates = results
        .filter((r) => r.hasFields)
        .map((r) => r.season)
        .sort((a, b) => b.to.getTime() - a.to.getTime());
      setCopyCandidates(candidates);
      setCopyFrom(candidates.length > 0 ? candidates[0].id : DONT_COPY);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rest.visible, seasons]);

  const disabled = from == null || to == null || from.getTime() >= to.getTime();

  const copyOptions: TOption[] = [
    { title: lang.seasons.dontCopyOption, value: DONT_COPY },
    ...copyCandidates.map((s) => ({
      title: seasonName(s.from, s.to),
      value: s.id,
    })),
  ];

  return (
    <Dialog
      style={{ width: 336 }}
      title={lang.seasons.createDialogTitle}
      {...rest}
    >
      <Input
        value={(from && to) ? seasonName(from!, to!) : ""}
        disabled={true}
        label={lang.seasons.nameTitle}
      />
      <SizedBox height={16} />
      <Row>
        <DatePickerInput value={from} onChange={setStartDate} />
        <SizedBox width={16} />
        <DatePickerInput value={to} onChange={setEndDate} />
      </Row>
      {copyCandidates.length > 0 && (
        <>
          <SizedBox height={16} />
          <Select
            label={lang.seasons.copyFromLabel}
            options={copyOptions}
            value={copyFrom}
            onChange={setCopyFrom}
          />
        </>
      )}
      <SizedBox height={24} />
      <Row>
        <Button kind="outline" onClick={rest.onClose}>
          {lang.seasons.cancelButton}
        </Button>
        <SizedBox width={16} />
        <Button
          onClick={() => onSubmit(from!, to!, copyFrom || undefined)}
          disabled={disabled}
        >
          {lang.seasons.createButton}
        </Button>
      </Row>
    </Dialog>
  );
};
export default observer(CreateSeasonDialog);
